import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import AuthLayout from "@/components/layouts/AuthLayout";
import { supabase } from "@/supabase";
import { useQuery } from "@tanstack/react-query";
import { managementCompanyApi } from "@/services/api/organizationApi";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ManagementCompany } from "@/models";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useAuth } from "@/contexts/AuthContext";
const loginSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address"
  }),
  password: z.string().min(6, {
    message: "Password must be at least 6 characters"
  }),
  managementCompanyId: z.coerce.number().optional()
});
type LoginFormValues = z.infer<typeof loginSchema>;
const Login = () => {
  const {
    login
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    toast
  } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      managementCompanyId: undefined
    }
  });

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    setError(null);
    try {
      // Handle both demo users and real users with the login function from context
      await login(data.email, data.password);
      navigate("/backoffice/dashboard");
      toast({
        title: "Login successful",
        description: "You have been successfully logged in."
      });
    } catch (error: any) {
      console.error("Login error:", error);
      setError(error.message || "Invalid email or password");
      toast({
        variant: "destructive",
        title: "Login failed",
        description: error.message || "Invalid email or password. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };
  return <AuthLayout title="Sign in to your account" subtitle="Or create a new account if you don't have one">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {error && <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md">
              {error}
            </div>}
          
          <FormField control={form.control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="your@email.com" type="email" {...field} onChange={e => {
              field.onChange(e);
            }} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={form.control} name="password" render={({
          field
        }) => <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Password</FormLabel>
                  <Link to="/forgot-password" className="text-sm text-brand-blue hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <FormControl>
                  <Input placeholder="••••••••" type="password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </> : "Sign in"}
          </Button>

          

          
        </form>
      </Form>
    </AuthLayout>;
};
export default Login;